/**
 * MicrophoneCheck - Microphone verification step
 * Nordic Sanctuary design: Clear feedback, audio visualization
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Mic, MicOff, RefreshCw, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@jourfx/ui";

interface MicrophoneCheckProps {
  onResult: (passed: boolean) => void;
  onContinue: () => void;
}

export function MicrophoneCheck({ onResult, onContinue }: MicrophoneCheckProps) {
  const { t } = useTranslation("techcheck");
  const [status, setStatus] = useState<"checking" | "listening" | "pass" | "fail">("checking");
  const [error, setError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const checkMicrophone = async () => {
    setStatus("checking");
    setError(null);

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      streamRef.current = mediaStream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(mediaStream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      setStatus("listening");

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let detected = false;

      const checkLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        const normalizedLevel = Math.min(average / 128, 1);
        setAudioLevel(normalizedLevel);

        if (normalizedLevel > 0.15 && !detected) {
          detected = true;
          setStatus("pass");
          onResult(true);
        }

        animationRef.current = requestAnimationFrame(checkLevel);
      };

      checkLevel();

      setTimeout(() => {
        if (!detected) {
          setStatus("fail");
          setError(t("checks.microphone.noSound"));
          onResult(false);
        }
      }, 10000);
    } catch (err) {
      console.error("Microphone check failed:", err);
      setStatus("fail");
      onResult(false);

      const domError = err as DOMException;
      if (domError.name === "NotAllowedError") {
        setError(t("checks.microphone.permissionDenied"));
      } else if (domError.name === "NotFoundError") {
        setError(t("checks.microphone.notFound"));
      } else {
        setError(t("checks.microphone.genericError"));
      }
    }
  };

  useEffect(() => {
    checkMicrophone();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const handleRetry = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    checkMicrophone();
  };

  // Generate bar heights for visualizer
  const bars = 5;
  const getBarHeight = (index: number) => {
    const baseHeight = 8;
    const maxExtra = 32;
    const variation = Math.sin((index / bars) * Math.PI) * 0.5 + 0.5;
    return baseHeight + audioLevel * maxExtra * variation;
  };

  return (
    <div className="space-y-6">
      {/* Microphone indicator */}
      <div className="flex flex-col items-center py-6">
        <div
          className={`
            w-24 h-24 rounded-3xl flex items-center justify-center transition-all duration-300
            ${
              status === "pass"
                ? "bg-[var(--sage-500)] shadow-lg shadow-[var(--sage-300)]/30"
                : status === "fail"
                  ? "bg-[oklch(0.95_0.04_25)]"
                  : "bg-[var(--sage-100)]"
            }
          `}
        >
          {status === "fail" ? (
            <MicOff
              className="w-10 h-10 text-[oklch(0.55_0.18_25)]"
              aria-hidden="true"
            />
          ) : (
            <Mic
              className={`w-10 h-10 ${status === "pass" ? "text-white" : "text-[var(--sage-600)]"}`}
              aria-hidden="true"
            />
          )}
        </div>

        {/* Audio level visualizer */}
        {(status === "listening" || status === "pass") && (
          <div className="mt-6 flex items-end justify-center gap-1 h-12">
            {Array.from({ length: bars }).map((_, i) => (
              <div
                key={i}
                className={`w-3 rounded-full transition-all duration-75 ${
                  status === "pass" ? "bg-[var(--sage-500)]" : "bg-[var(--sage-400)]"
                }`}
                style={{ height: `${getBarHeight(i)}px` }}
              />
            ))}
          </div>
        )}

        {/* Status text */}
        <div className="mt-4 text-center">
          {status === "checking" && (
            <p className="text-[var(--stone-500)]">{t("checks.microphone.checking")}</p>
          )}
          {status === "listening" && (
            <p className="text-[var(--stone-600)] font-medium">{t("checks.microphone.listening")}</p>
          )}
          {status === "pass" && (
            <div className="flex items-center gap-2 text-[var(--sage-600)]">
              <CheckCircle className="w-5 h-5" aria-hidden="true" />
              <span className="font-semibold">{t("checks.microphone.success")}</span>
            </div>
          )}
          {status === "fail" && error && (
            <p className="text-[oklch(0.50_0.15_25)] max-w-sm">{error}</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        {status === "fail" && (
          <Button variant="secondary" size="client" className="flex-1" onClick={handleRetry}>
            <RefreshCw aria-hidden="true" />
            {t("actions.retry")}
          </Button>
        )}
        <Button
          variant={status === "pass" ? "default" : "secondary"}
          size="client"
          className="flex-1"
          onClick={onContinue}
          disabled={status === "checking"}
        >
          {status === "pass" ? (
            <>
              {t("actions.continue")}
              <ArrowRight aria-hidden="true" />
            </>
          ) : (
            t("actions.skip")
          )}
        </Button>
      </div>

      {status === "fail" && (
        <div className="rounded-xl border border-[var(--stone-200)] bg-[var(--stone-50)] p-4">
          <p className="text-xs text-[var(--stone-600)]">{t("troubleshooting.microphone")}</p>
        </div>
      )}
    </div>
  );
}
